import * as tslib_1 from "tslib";
// Core packages
import { HttpResponse } from "@angular/common/http";
import { OnDestroy, OnInit } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material";
// Third party packages
import { TranslateService } from "@ngx-translate/core";
import * as FileSaver from "file-saver";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { finalize } from "rxjs/operators";
// Custom packages
import { DocumentArchiveDocsService } from "src/app/modules/properties/document-archive-docs.service";
import { dossierFilesCountMessage, dossierListErrorMessage, dossierPartErrorMessage, dossierPartRows, dossierPartSaveName, dossierPartSizeLabel, shouldReloadDossierParts, } from "src/app/modules/properties/document-archive-dossier.utils";
import { skippedFilesCount, skippedFilesMessage, SKIPPED_FILES_HEADER, } from "src/app/modules/properties/document-files-zip.utils";
import { ConfigService } from "src/app/shared/services/config.service";
/**
 * The dossier zip of a property on the 3.0.0 archive, part by part (NEW-M17).
 *
 * It stands in for the legacy «Esporta file» dialog (`ExportCdDocsDialogComponent`) once the archive is
 * switched on. The legacy one asks `GET properties/:id/export-file`, which builds every zip of the other
 * archive into the public `/export` folder, and offers their public links. Here the dialog reads the
 * list of the parts (`GET /document-archive-docs/export/dossier`) and each «Scarica» asks for ONE part
 * with the session (`GET /export/dossier/:part`), saved from the blob: no path is ever public, and no
 * zip exists before it is asked for.
 *
 * One part at a time (decision of Luis, 27 October 2026): while a part downloads, the other buttons are
 * off. A part holds about 500 MB of files and arrives whole in the memory of the browser, and the list
 * and the parts share one per-address limit of 30 requests in 10 minutes.
 *
 * The list is shown whole or not at all (@see dossierPartRows). A 404 on a part reads the list again:
 * the back-end composes the dossier on every request, so a part can be gone between the list and the
 * download.
 *
 * The dialog can be closed while a part downloads, as «Scarica i documenti»: nothing is saved then.
 * ApiService does not cancel the request on unsubscribe, so the transfer still runs to its end before
 * the answer is dropped.
 *
 * Declared in DocumentArchiveDossierDialogModule, imported by PropertiesModule: it is in the initial
 * bundle with the pages that open it.
 *
 * @since 3.0.0
 */
var DocumentArchiveDossierDialogComponent = /** @class */ (function () {
    /**
     * Class constructor
     */
    function DocumentArchiveDossierDialogComponent(data, dialogRef, documentArchiveDocsService, configService, cookieService, translateService, toastrService) {
        this.data = data;
        this.dialogRef = dialogRef;
        this.documentArchiveDocsService = documentArchiveDocsService;
        this.configService = configService;
        this.cookieService = cookieService;
        this.translateService = translateService;
        this.toastrService = toastrService;
        this.subscriptions = [];
        /** Language of the session, for the decimal separator of the weights */
        this.lang = "it";
        this.state = "loading";
        this.parts = [];
        /** Why the list could not be read; null unless `state` is "failed" */
        this.failure = null;
        /** The part on its way, null when none: only one part downloads at a time */
        this.downloadingPart = null;
    }
    /**
     * Read the list of the parts.
     *
     * @since 3.0.0
     */
    DocumentArchiveDossierDialogComponent.prototype.ngOnInit = function () {
        this.lang = this.cookieService.get("lang") || this.configService.defaultLang;
        this.loadParts();
    };
    /**
     * Destroy component: a part on its way is no longer saved (@see DocumentArchiveDossierDialogComponent).
     *
     * @since 3.0.0
     */
    DocumentArchiveDossierDialogComponent.prototype.ngOnDestroy = function () {
        this.subscriptions.forEach(function (sub) { return sub.unsubscribe(); });
    };
    /**
     * Read the list again after a failure.
     *
     * @since 3.0.0
     */
    DocumentArchiveDossierDialogComponent.prototype.onRetry = function () {
        if (this.state !== "failed") {
            return;
        }
        this.loadParts();
    };
    /**
     * Download one part.
     *
     * The zip is saved under the name the back-end sends in `Content-Disposition`, and a warning tells how
     * many files it could not include, as «Scarica tutti» does. The dialog stays open: a dossier of
     * several parts is downloaded part after part.
     *
     * @since 3.0.0
     *
     * @param part The part whose «Scarica» was clicked
     */
    DocumentArchiveDossierDialogComponent.prototype.onDownload = function (part) {
        var _this = this;
        if (!part || this.state !== "loaded" || this.downloadingPart !== null) {
            return;
        }
        this.downloadingPart = part.part;
        this.subscriptions.push(this.documentArchiveDocsService
            .downloadDossierPart(this.propertyId, part.part)
            .pipe(finalize(function () { return (_this.downloadingPart = null); }))
            .subscribe(function (res) {
            // ApiService emits a failure without status (network down, connection cut once the
            // zip had started) as a value, not as an error
            if (!(res instanceof HttpResponse) || !(res.body instanceof Blob)) {
                console.warn("Fascicolo (v3): part download failed", { status: 0 });
                _this.showMessage(dossierPartErrorMessage(0, null, Date.now()), "error");
                return;
            }
            _this.saveBlob(res.body, dossierPartSaveName(res.headers.get("Content-Disposition"), part.fileName));
            var skipped = skippedFilesMessage(skippedFilesCount(res.headers.get(SKIPPED_FILES_HEADER)));
            if (skipped) {
                _this.showMessage(skipped, "warning");
            }
        }, function (err) {
            var status = err && typeof err.status === "number" ? err.status : 0;
            var retryAfter = err && err.headers ? err.headers.get("Retry-After") : null;
            console.warn("Fascicolo (v3): part download failed", { status: status });
            _this.showMessage(dossierPartErrorMessage(status, retryAfter, Date.now()), "error");
            if (shouldReloadDossierParts(status)) {
                _this.loadParts();
            }
        }));
    };
    /**
     * Close the dialog.
     *
     * @since 3.0.0
     */
    DocumentArchiveDossierDialogComponent.prototype.onClose = function () {
        this.dialogRef.close();
    };
    Object.defineProperty(DocumentArchiveDossierDialogComponent.prototype, "propertyId", {
        /** The property of the dialog, empty when the opener passed none */
        get: function () {
            return this.data && typeof this.data.propertyId === "string" ? this.data.propertyId.trim() : "";
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Ask for the list of the parts and show it, or say why it could not be read.
     *
     * Without a property nothing is asked: there is no dossier to name.
     */
    DocumentArchiveDossierDialogComponent.prototype.loadParts = function () {
        var _this = this;
        this.parts = [];
        this.failure = null;
        var propertyId = this.propertyId;
        if (!propertyId) {
            this.state = "failed";
            this.failure = dossierListErrorMessage(0, null, Date.now());
            return;
        }
        this.state = "loading";
        this.subscriptions.push(this.documentArchiveDocsService.dossierParts(propertyId).subscribe(function (res) {
            var rows = res && res.status ? dossierPartRows(res.data) : null;
            if (rows === null) {
                console.warn("Fascicolo (v3): parts list unreadable", { status: 200 });
                _this.state = "failed";
                _this.failure = dossierListErrorMessage(0, null, Date.now());
                return;
            }
            _this.parts = rows.map(function (row) { return (tslib_1.__assign({}, row, { filesMessage: dossierFilesCountMessage(row.files), sizeText: dossierPartSizeLabel(row.bytes, _this.lang) })); });
            _this.state = "loaded";
        }, function (err) {
            // The dialog says it in its body; a toast on top of it would only repeat it
            var status = err && typeof err.status === "number" ? err.status : 0;
            var retryAfter = err && err.headers ? err.headers.get("Retry-After") : null;
            console.warn("Fascicolo (v3): parts list failed", { status: status });
            _this.state = "failed";
            _this.failure = dossierListErrorMessage(status, retryAfter, Date.now());
        }));
    };
    /**
     * Save a downloaded part. The blob is handed to FileSaver as a BLOB and not as a URL, which is what
     * keeps the library from probing the server with a synchronous HEAD request first.
     */
    DocumentArchiveDossierDialogComponent.prototype.saveBlob = function (blob, fileName) {
        FileSaver.saveAs(blob, fileName);
    };
    /**
     * Show a translated toast of a download
     */
    DocumentArchiveDossierDialogComponent.prototype.showMessage = function (message, level) {
        var text = this.translateService.instant(message.key, message.params);
        if (level === "warning") {
            this.toastrService.warning(text);
        }
        else {
            this.toastrService.error(text);
        }
    };
    return DocumentArchiveDossierDialogComponent;
}());
export { DocumentArchiveDossierDialogComponent };
/**
 * Open the dossier dialog of a property on the 3.0.0 archive, with the size and the closing rule of the
 * legacy «Esporta file» dialog it stands in for: closed by «Chiudi» only.
 *
 * The four buttons that open the legacy dialog — «Scarica archivio fascicolo documentale» of the
 * property home and «Esporta file» of the property list, of the suspended list and of the dialog of the
 * statistics — call it instead once the archive is switched on (NEW-M17).
 *
 * @since 3.0.0
 *
 * @param dialog     MatDialog of the page that opens it
 * @param propertyId The property whose dossier is downloaded
 */
export function openDocumentArchiveDossierDialog(dialog, propertyId) {
    var data = { propertyId: propertyId };
    return dialog.open(DocumentArchiveDossierDialogComponent, {
        minWidth: "50%",
        maxHeight: "98vh",
        data: data,
        disableClose: true,
    });
}
